import Communicator from "../../../Helper/Communicator";
import {
  translateHelpCenterText,
  useHelpCenterStore,
} from "../../../Stores/HelpCenter";
import { useConfigStore } from "../../../Stores/Config";
import { useRouterStore } from "../../../Stores/Router";
import { activateOnKey } from "../../../Helper/a11y";

export const ChevronRight = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m9 18 6-6-6-6" />
  </svg>
);

export const Loading = () => (
  <div className="helpcenter-loading">
    <div className="helpcenter-loading-spinner" />
  </div>
);

// An article or collection that did not load: deleted, unpublished, or a help
// center the plan no longer includes (the API answers 404). Shown instead of
// the spinner, which used to stay forever; the header's back button still
// leads out. Same wording and style as an empty help center search.
export const NotFound = () => {
  useConfigStore((s) => s.config); // re-render on language / translation changes
  return (
    <div className="helpcenter-search-results-empty" role="status">
      {translateHelpCenterText(
        "nothingFoundTitle",
        "nothingFoundTitle",
        "Nothing found",
      )}
    </div>
  );
};

export function ArticleItem({
  article,
  isCollection,
  active,
  id,
}: {
  article: any;
  isCollection?: boolean;
  /** Picked with the arrow keys in the help center search (Enter opens it). */
  active?: boolean;
  id?: string;
}) {
  // Search hits carry an optional `highlight` ({ title, description, content })
  // with matched terms wrapped in <mark>, HTML-escaped server-side (safe to
  // render as HTML). Collection/article listings have no highlight → title only.
  const highlight = article?.highlight ?? null;
  const highlightedTitle: string | null = highlight?.title ?? null;
  // Prefer a body snippet (match in context) over the description.
  const highlightedSnippet: string | null =
    highlight?.content ?? highlight?.description ?? null;
  const plainDescription =
    !isCollection && article?.description && article.description !== "--"
      ? article.description
      : "";
  const showSnippet =
    !isCollection && Boolean(highlightedSnippet || plainDescription);

  const navigate = () => {
    if (isCollection) {
      useRouterStore.getState().pushPage("helpcenter-collection", {
        id: article.docId ?? article.id,
      });
    } else {
      useHelpCenterStore.getState().openArticle(article.docId ?? article.id);
    }
  };

  return (
    <div
      id={id}
      className={`helpcenter-article-item${active ? " helpcenter-article-item--active" : ""}`}
      role="button"
      tabIndex={0}
      onClick={navigate}
      onKeyDown={activateOnKey(navigate)}
    >
      <div className="helpcenter-article-item-details">
        <div className="helpcenter-article-item-title">
          {highlightedTitle ? (
            <span dangerouslySetInnerHTML={{ __html: highlightedTitle }} />
          ) : (
            article.title
          )}
        </div>
        {showSnippet && (
          <div className="helpcenter-article-item-description">
            {highlightedSnippet ? (
              <span dangerouslySetInnerHTML={{ __html: highlightedSnippet }} />
            ) : (
              plainDescription
            )}
          </div>
        )}
      </div>
      <div className="helpcenter-article-item-chevron">
        <ChevronRight />
      </div>
    </div>
  );
}
